import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AuthorityCard from './AuthorityCard';
import AmazonCard from './AmazonCard';

const DURATION = 10000;

const shuffleArray = (array) => {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const useVisibleCount = () => {
  const [count, setCount] = useState(typeof window !== 'undefined' && window.innerWidth >= 768 ? 3 : 1);
  useEffect(() => {
    const update = () => setCount(window.innerWidth >= 768 ? 3 : 1);
    window.addEventListener('resize', update);
    update();
    return () => window.removeEventListener('resize', update);
  }, []);
  return count;
};

const ReviewCarousel = ({ reviews }) => {
  const [shuffledReviews, setShuffledReviews] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const visible = useVisibleCount();

  const startRef = useRef(null);
  const rafRef = useRef(null);
  const pausedAtRef = useRef(0);

  useEffect(() => {
    setShuffledReviews(shuffleArray(reviews));
    setPageIndex(0);
  }, [reviews]);

  const total = shuffledReviews.length;
  const maxPage = Math.max(0, Math.ceil(total / visible) - 1);

  const resetTimer = () => {
    setProgress(0);
    pausedAtRef.current = 0;
    startRef.current = performance.now();
  };

  const next = useCallback(() => {
    setDirection(1);
    setPageIndex(i => (i >= maxPage ? 0 : i + 1));
    resetTimer();
  }, [maxPage]);

  const prev = useCallback(() => {
    setDirection(-1);
    setPageIndex(i => (i <= 0 ? maxPage : i - 1));
    resetTimer();
  }, [maxPage]);

  const goTo = useCallback((newPage) => {
    setDirection(newPage > pageIndex ? 1 : -1);
    setPageIndex(newPage);
    resetTimer();
  }, [pageIndex]);

  // Progress animation
  useEffect(() => {
    if (total === 0) return;
    startRef.current = performance.now();
    pausedAtRef.current = 0;
    cancelAnimationFrame(rafRef.current);

    const tick = (now) => {
      if (paused) return;
      const elapsed = (now - startRef.current) + pausedAtRef.current;
      const pct = Math.min(elapsed / DURATION, 1);
      setProgress(pct);
      if (pct >= 1) { next(); return; }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [pageIndex, total, next]);

  useEffect(() => {
    if (total === 0) return;
    if (paused) {
      cancelAnimationFrame(rafRef.current);
      pausedAtRef.current = progress * DURATION;
    } else {
      startRef.current = performance.now();
      const tick = (now) => {
        const elapsed = (now - startRef.current) + pausedAtRef.current;
        const pct = Math.min(elapsed / DURATION, 1);
        setProgress(pct);
        if (pct >= 1) { next(); return; }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    }
    return () => cancelAnimationFrame(rafRef.current);
  }, [paused, total]);

  if (total === 0) return null;

  const startIndex = pageIndex * visible;
  const visibleCards = shuffledReviews.slice(startIndex, startIndex + visible);
  if (visibleCards.length < visible) {
    visibleCards.push(...shuffledReviews.slice(0, visible - visibleCards.length));
  }

  const variants = {
    enter: d => ({ x: d > 0 ? 50 : -50, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: d => ({ x: d > 0 ? -50 : 50, opacity: 0 }),
  };

  const isNearEnd = progress > 0.82;

  return (
    <div
      className="w-full flex flex-col"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Cards */}
      <div className="relative overflow-hidden w-full">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={`${pageIndex}-${visible}`}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.5, ease: 'easeInOut' }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6"
          >
            {visibleCards.map((review, i) =>
              review.type === 'critic'
                ? <AuthorityCard key={`c-${review.id}-${i}`} review={review} index={i} />
                : <AmazonCard key={`a-${review.id}-${i}`} review={review} index={i} />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-center gap-4 mt-5">
        <button
          onClick={prev}
          className="w-8 h-8 rounded-full border border-gold/50 flex items-center justify-center text-gold text-xl hover:bg-gold/10 hover:border-gold transition-all duration-300"
          aria-label="Previous"
        >‹</button>

        <div className="flex gap-2 flex-wrap justify-center">
          {Array.from({ length: maxPage + 1 }).map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === pageIndex ? 'w-7 bg-gold' : 'w-2 bg-gold/25'
              }`}
              aria-label={`Page ${i + 1}`}
            />
          ))}
        </div>

        <button
          onClick={next}
          className="w-8 h-8 rounded-full border border-gold/50 flex items-center justify-center text-gold text-xl hover:bg-gold/10 hover:border-gold transition-all duration-300"
          aria-label="Next"
        >›</button>
      </div>

      {/* Progress Bar */}
      <div className="mt-4 relative h-7">
        <div className="h-px w-full rounded-full overflow-hidden" style={{ background: 'rgba(212,175,55,0.12)' }}>
          <div
            className="h-full rounded-full"
            style={{
              width: `${progress * 100}%`,
              background: isNearEnd ? 'linear-gradient(90deg, #D4AF37, #fff8d6)' : 'rgba(212,175,55,0.55)',
              boxShadow: isNearEnd ? '0 0 10px rgba(212,175,55,0.8)' : 'none',
              transition: 'none',
            }}
          />
        </div>
        {isNearEnd && !paused && (
          <motion.p
            initial={{ opacity: 0, y: 3 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center font-lexend text-[9px] tracking-widest uppercase mt-1.5 absolute w-full"
            style={{ color: 'rgba(212,175,55,0.6)' }}
          >
            Next set arriving…
          </motion.p>
        )}
        {paused && (
          <p
            className="text-center font-lexend text-[9px] tracking-widest uppercase mt-1.5 absolute w-full hidden md:block"
            style={{ color: 'rgba(212,175,55,0.35)' }}
          >
            Paused — hover away to resume
          </p>
        )}
      </div>
    </div>
  );
};

export default ReviewCarousel;